"use client";

import Script from "next/script";
import { useEffect, useState, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { isProductionClient } from "@/lib/site-env";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Manual Google Analytics 4 wiring.
 *
 * We don't use @next/third-parties because adding it required a lockfile
 * regen that Railway's `npm ci` can't perform. Instead we inline the gtag
 * loader and fire page_view manually on App Router route changes (Next's
 * client router doesn't trigger gtag's auto page_view).
 */

function GoogleAnalyticsTracker({ gaId }: { gaId: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window === "undefined" || !window.gtag) return;
    const url = pathname + (searchParams?.toString() ? `?${searchParams}` : "");
    window.gtag("event", "page_view", {
      page_path: url,
      page_location: window.location.href,
      page_title: document.title,
      send_to: gaId,
    });
  }, [pathname, searchParams, gaId]);

  return null;
}

export default function GoogleAnalytics({ gaId }: { gaId: string }) {
  // Analytics is production-only: non-production deploys (Railway dev,
  // previews, localhost) must never report into the live GA property. Resolved
  // once at first render — no effect, so no cascading re-render. `afterInteractive`
  // scripts are injected by Next's client runtime rather than emitted into the
  // SSR HTML, so returning null here costs no hydration mismatch.
  const [enabled] = useState(isProductionClient);

  if (!enabled) return null;

  return (
    <>
      {/* gtag.js loader — async, deferred behind Next's afterInteractive */}
      <Script
        id="ga4-loader"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
      />
      {/* Initial config — send_page_view: false so our tracker handles it
          (otherwise we'd double-count on initial load + every navigation) */}
      <Script
        id="ga4-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = gtag;
            gtag('js', new Date());
            gtag('config', '${gaId}', { send_page_view: false });
          `,
        }}
      />
      {/* useSearchParams needs Suspense in App Router */}
      <Suspense fallback={null}>
        <GoogleAnalyticsTracker gaId={gaId} />
      </Suspense>
    </>
  );
}
